import { supabase } from "@/integrations/supabase/client";

// Feature flag names as constants
export const FLAGS = {
  ASYNC_TEST_RUN: 'ff_async_test_run',
  TEST_RUN_CANCEL: 'ff_test_run_cancel',
  TEST_RUN_PROGRESS_PANEL: 'ff_test_run_progress_panel',
  BATCH_HEALTH_REPORT: 'ff_batch_health_report',
  ADMIN_PRODUCTION_RUN: 'enable_admin_production_run',
  FLYER_DIAGNOSTICS_V1: 'ff_flyer_diagnostics_v1', // Deprecated - V2 is the active system
  // CR001: Spend & Earn flags
  SPEND_EARN_SCANNING: 'enable_spend_earn_scanning',
  SPEND_EARN_UI: 'enable_spend_earn_ui',
  // CR309: Directory gate for scan targets
  SCAN_TARGETS_USE_DIRECTORY_GATE: 'scan_targets_use_directory_gate',
  // CR550: Region filter in Merchant Directory
  REGIONS_FILTER: 'ff_regions_filter',
  // CR384: Flyer capture image modes (IMAGE_ONLY, IMAGE_AND_DOWNLOAD)
  FLYER_CAPTURE_IMAGE_MODES: 'ff_flyer_capture_image_modes',
  // CR406: Cookbook gate remediation (no-AI)
  GATE_COOKBOOK_V1: 'ff_gate_cookbook_v1',
  // CR407: Pattern lifecycle auto-promote/degrade
  GATE_PATTERNS_AUTO_PROMOTE: 'ff_gate_patterns_auto_promote_v1',
  GATE_PATTERNS_AUTO_DEGRADE: 'ff_gate_patterns_auto_degrade_v1',
  // CR408: Gate Patterns Admin UI
  GATE_PATTERNS_ADMIN: 'ff_gate_patterns_admin_v1',
  // CR416: BONUS reward brand defaults to promo merchant when unknown
  BONUS_REWARD_DEFAULT_TO_PROMO_MERCHANT: 'ff_bonus_reward_default_to_promo_merchant',
  // CR413: Structured eligibility requirements for back-face bullets
  ELIGIBILITY_REQUIREMENTS_V1: 'ff_eligibility_requirements_v1',
  // CR313: V2 Preview Contract debug panel
  V2_PREVIEW_CONTRACT: 'ff_v2_preview_contract',
  // CR453: Flyer pages v3 split tables dual-write
  FLYER_PAGES_V3_TABLES: 'ff_flyer_pages_v3_tables',
} as const;

export type FeatureFlagName = typeof FLAGS[keyof typeof FLAGS];

// Cache for flags to avoid repeated DB calls
const flagsCache: Map<string, boolean> = new Map();
let cacheTimestamp: number = 0;
const CACHE_TTL_MS = 60000; // 1 minute cache

/**
 * Check if a feature flag is enabled
 * Falls back to false if flag doesn't exist (safe default)
 */
export async function isFeatureEnabled(flagName: FeatureFlagName): Promise<boolean> {
  // Check cache first
  const now = Date.now();
  if (now - cacheTimestamp < CACHE_TTL_MS && flagsCache.has(flagName)) {
    return flagsCache.get(flagName) ?? false;
  }

  try {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('enabled')
      .eq('flag_name', flagName)
      .maybeSingle();

    if (error) {
      console.error(`Error fetching feature flag ${flagName}:`, error);
      return false;
    }

    const enabled = data?.enabled ?? false;
    flagsCache.set(flagName, enabled);
    cacheTimestamp = now;
    
    return enabled;
  } catch (err) {
    console.error(`Error checking feature flag ${flagName}:`, err);
    return false;
  }
}

/**
 * Get all feature flags at once (more efficient for initial load)
 */
export async function getAllFeatureFlags(): Promise<Record<string, boolean>> {
  try {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('flag_name, enabled');

    if (error) {
      console.error('Error fetching feature flags:', error);
      return {};
    }

    const flags: Record<string, boolean> = {};
    for (const flag of data || []) {
      flags[flag.flag_name] = flag.enabled;
      flagsCache.set(flag.flag_name, flag.enabled);
    }
    cacheTimestamp = Date.now();

    // Log active flags for debugging
    const activeFlags = Object.entries(flags)
      .filter(([_, enabled]) => enabled)
      .map(([name]) => name);
    
    if (activeFlags.length > 0) {
      console.log('[Feature Flags] Active:', activeFlags.join(', '));
    }

    return flags;
  } catch (err) {
    console.error('Error fetching all feature flags:', err);
    return {};
  }
}

/**
 * Hook-friendly wrapper for checking multiple flags at once
 */
export function useFeatureFlags() {
  return {
    isEnabled: isFeatureEnabled,
    getAll: getAllFeatureFlags,
    FLAGS,
  };
}

/**
 * Clear the cache (useful for testing or after admin updates)
 */
export function clearFlagCache() {
  flagsCache.clear();
  cacheTimestamp = 0;
}
